import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import {
  isNativeApp,
  inAppBrowserNavigator,
  openInAppBrowser,
  NATIVE_REDIRECT_URI,
  NATIVE_LOGOUT_URI,
} from "./nativeApp";

// Instância única do UserManager: usada pelo AuthProvider (index.js) e fora do
// React (interceptor do axios), garantindo uma só fonte de verdade para o token.
// No app (Capacitor) o login abre no navegador in-app e volta pelo custom scheme.
export const userManager = new UserManager({
  authority: "https://cognito-idp.us-east-2.amazonaws.com/us-east-2_EPGMOKNJ8",
  client_id: "4nr7am7blg9l45pipp40q8pj0h",
  redirect_uri: isNativeApp
    ? NATIVE_REDIRECT_URI
    : process.env.REACT_APP_BASE_URL || "http://localhost:3000",
  response_type: "code",
  scope: "email openid profile phone aws.cognito.signin.user.admin",
  // Renovação sob demanda (getValidAccessToken / 401): timers não são confiáveis
  // quando o app fica em background no iOS.
  automaticSilentRenew: false,
  // Evita loader infinito quando a rede trava (ex.: app voltando do background).
  requestTimeoutInSeconds: 15,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
}, isNativeApp ? inAppBrowserNavigator : undefined);

// Segundos de folga antes da expiração para já renovar o token
const EXPIRY_MARGIN_SECONDS = 30;

let renewing = null;

/**
 * Renova o token via refresh_token (nunca via iframe, que o Cognito bloqueia).
 * Chamadas concorrentes compartilham a mesma promise.
 * Refresh token ausente/inválido → remove o usuário (AuthGuard leva ao login).
 * Erros de rede apenas rejeitam, sem deslogar.
 */
export const renewToken = () => {
  if (!renewing) {
    renewing = (async () => {
      const user = await userManager.getUser();
      if (!user?.refresh_token) {
        await userManager.removeUser();
        throw new Error("No refresh token available");
      }
      try {
        const renewed = await userManager.signinSilent();
        if (!renewed?.access_token) throw new Error("No access token after renew");
        return renewed;
      } catch (err) {
        if (err?.error === "invalid_grant") {
          await userManager.removeUser();
        }
        throw err;
      }
    })().finally(() => {
      renewing = null;
    });
  }
  return renewing;
};

// Retorna um access token válido, renovando antes se estiver expirado/expirando.
export const getValidAccessToken = async () => {
  const user = await userManager.getUser();
  if (!user) return null;
  if (user.expires_in === undefined || user.expires_in > EXPIRY_MARGIN_SECONDS) {
    return user.access_token;
  }
  const renewed = await renewToken();
  return renewed.access_token;
};

// Domínio do Cognito Hosted UI (endpoint /logout encerra a sessão do Cognito)
const COGNITO_DOMAIN = "https://auth.buildcheap.com.br";

/**
 * Logout completo: limpa a sessão local e encerra a sessão do Cognito, para que
 * o próximo login permita escolher outra conta. O logout_uri precisa estar em
 * LogoutURLs do app client (template-auth.yaml).
 */
export const signOut = async () => {
  const { client_id, redirect_uri } = userManager.settings;
  const logoutUri = isNativeApp ? NATIVE_LOGOUT_URI : redirect_uri;
  const url = `${COGNITO_DOMAIN}/logout?client_id=${encodeURIComponent(client_id)}&logout_uri=${encodeURIComponent(logoutUri)}`;
  await userManager.removeUser();
  // No app, a sessão do Cognito vive no navegador in-app; o AuthGuard leva ao /login
  if (isNativeApp) {
    await openInAppBrowser(url);
  } else {
    window.location.assign(url);
  }
};
